// File: frontend/src/components/STIX/BundlePreviewModal.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Paper,
  alpha,
  styled,
  IconButton,
  Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { STIXBundleEnriched, STIXBundle, STIXObject } from '../../types/stix';

interface BundlePreviewModalProps {
  open: boolean;
  bundle: STIXBundleEnriched | null;
  onClose: () => void;
  onFetchBundle: (articleId: string) => Promise<STIXBundle>;
  onDownload: (articleId: string, title?: string) => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    maxWidth: '900px',
    width: '100%',
  }
}));

const SectionBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  background: alpha(theme.palette.background.default, 0.5),
  borderRadius: theme.spacing(1),
}));

const ObjectChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  fontSize: '0.75rem',
}));

const getObjectTypeIcon = (type: string): string => {
  const icons: { [key: string]: string } = {
    'indicator': '🛡️',
    'malware': '🦠',
    'vulnerability': '🐛',
    'attack-pattern': '🎯',
    'threat-actor': '👤',
    'relationship': '🔗',
    'tool': '🔧',
    'report': '📄'
  };
  return icons[type] || '📦';
};

const groupObjectsByType = (objects: STIXObject[]) => {
  const grouped: { [key: string]: STIXObject[] } = {};
  objects.forEach(obj => {
    if (!grouped[obj.type]) {
      grouped[obj.type] = [];
    }
    grouped[obj.type].push(obj);
  });
  return grouped;
};

export const BundlePreviewModal: React.FC<BundlePreviewModalProps> = ({
  open,
  bundle,
  onClose,
  onFetchBundle,
  onDownload
}) => {
  const [loading, setLoading] = useState(false);
  const [fullBundle, setFullBundle] = useState<STIXBundle | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadBundle = useCallback(async () => {
    if (!bundle) return;

    setLoading(true);
    setError(null);
    try {
      const data = await onFetchBundle(bundle.articleId);
      setFullBundle(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bundle');
    } finally {
      setLoading(false);
    }
  }, [bundle, onFetchBundle]);

  useEffect(() => {
    if (open && bundle) {
      loadBundle();
    }
  }, [open, bundle, loadBundle]);

  const handleClose = () => {
    setFullBundle(null);
    setError(null);
    onClose();
  };

  if (!bundle) return null;

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={4} textAlign="center">
          <Typography color="error">{error}</Typography>
          <Button onClick={loadBundle} sx={{ mt: 2 }}>
            Retry
          </Button>
        </Box>
      );
    }

    if (!fullBundle) return null;

    const groupedObjects = groupObjectsByType(fullBundle.objects);
    const malware = groupedObjects['malware'] || [];
    const indicators = groupedObjects['indicator'] || [];
    const vulnerabilities = groupedObjects['vulnerability'] || [];
    const attackPatterns = groupedObjects['attack-pattern'] || [];
    const relationships = groupedObjects['relationship'] || [];

    return (
      <>
        {/* Summary Section */}
        <SectionBox>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            📊 Summary
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Total Objects: <strong>{fullBundle.objects.length}</strong>
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
            {Object.entries(bundle.objectCounts)
              .filter(([_, count]) => count > 0)
              .map(([type, count]) => (
                <ObjectChip
                  key={type}
                  label={`${getObjectTypeIcon(type)} ${count} ${type}${count > 1 ? 's' : ''}`}
                  color="primary"
                  variant="outlined"
                />
              ))}
          </Stack>
        </SectionBox>

        {/* Malware Section */}
        {malware.length > 0 && (
          <SectionBox>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              🦠 Malware Objects ({malware.length})
            </Typography>
            <List dense>
              {malware.slice(0, 5).map((obj) => (
                <ListItem key={obj.id}>
                  <ListItemText
                    primary={obj.name || 'Unnamed'}
                    secondary={
                      <>
                        <Typography variant="caption" component="span" display="block">
                          ID: {obj.id}
                        </Typography>
                        {obj.description && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {obj.description.substring(0, 100)}...
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
              {malware.length > 5 && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                  ... and {malware.length - 5} more
                </Typography>
              )}
            </List>
          </SectionBox>
        )}

        {/* Indicators Section */}
        {indicators.length > 0 && (
          <SectionBox>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              🛡️ Top Indicators ({indicators.length} total, showing 10)
            </Typography>
            <List dense>
              {indicators.slice(0, 10).map((obj) => (
                <ListItem key={obj.id}>
                  <ListItemText
                    primary={obj.name || 'Unnamed'}
                    secondary={
                      <Typography variant="caption" component="span">
                        {obj.pattern?.substring(0, 80)}...
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </SectionBox>
        )}

        {/* Vulnerabilities Section */}
        {vulnerabilities.length > 0 && (
          <SectionBox>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              🐛 Vulnerabilities ({vulnerabilities.length})
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {vulnerabilities.map((obj) => (
                <Chip key={obj.id} label={obj.name || 'Unnamed'} color="warning" size="small" />
              ))}
            </Stack>
          </SectionBox>
        )}

        {/* Attack Patterns Section */}
        {attackPatterns.length > 0 && (
          <SectionBox>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              🎯 Attack Patterns ({attackPatterns.length})
            </Typography>
            <List dense>
              {attackPatterns.slice(0, 5).map((obj) => (
                <ListItem key={obj.id}>
                  <ListItemText
                    primary={obj.name || 'Unnamed'}
                    secondary={obj.description?.substring(0, 100)}
                  />
                </ListItem>
              ))}
            </List>
          </SectionBox>
        )}

        {/* Relationships Section */}
        {relationships.length > 0 && (
          <SectionBox>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              🔗 Relationships ({relationships.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {relationships.length} relationship{relationships.length > 1 ? 's' : ''} connecting objects in this bundle
            </Typography>
          </SectionBox>
        )}
      </>
    );
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Bundle Preview
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {bundle.title || 'Untitled Report'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {bundle.id}
            </Typography>
          </Box>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {renderContent()}
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          startIcon={<DownloadIcon />}
          variant="contained"
          onClick={() => onDownload(bundle.articleId, bundle.title)}
        >
          Download Full Bundle JSON
        </Button>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default BundlePreviewModal;
